"use client";

import { useMemo, type CSSProperties } from "react";
import { SUPPORTED_CEX } from "@cede/types";
import { LinkExchangesList } from "./LinkExchangesList";
import { InfoCard } from "@/components/ui/info-card";
import { useOAuthSupportMap } from "@/hooks/exchangeAuthentication/useOAuthSupportMap";
import { useSupportedCexPerps } from "@/hooks/perps/cex-perps/useSupportedCexPerps";
import { cn } from "@/lib/utils";
import { useCreateAccountStore } from "@/store/accountStore";
import { STEPS, FLOW_TYPES, useConnectStore } from "@/store/connectStore";

interface LinkExchangesProps {
  className?: string;
  style?: CSSProperties;
  showCampaignCounts?: boolean;
  campaignCountsByExchange?: Record<string, number>;
  sortByCampaigns?: boolean;
  isStepActive?: boolean;
  innerClassName?: string;
  variant?: "card" | "plain";
  title?: string;
  titleClassName?: string;
}

export function LinkExchanges({
  className,
  style,
  showCampaignCounts = false,
  campaignCountsByExchange = {},
  sortByCampaigns = false,
  isStepActive = false,
  innerClassName,
  variant = "card",
  title,
  titleClassName,
}: LinkExchangesProps) {
  const { updateFormData } = useCreateAccountStore();
  const { setActiveStep, setOpenConnectModal, setExchangeIds, setFlowType } = useConnectStore();
  const oAuthSupportMap = useOAuthSupportMap();
  const supportedCexPerps = useSupportedCexPerps();
  const supportedCexs = useMemo(() => {
    return [...new Set([...Object.values(SUPPORTED_CEX), ...supportedCexPerps])];
  }, [supportedCexPerps]);

  const handleSelectCex = (cex: string) => {
    updateFormData({ selectedCex: cex });
    setFlowType(FLOW_TYPES.READY_TO_USE);
    const step = oAuthSupportMap[cex] ? STEPS.LinkAccount : STEPS.ApiKeys;
    setActiveStep(step);
    setExchangeIds(Object.values(SUPPORTED_CEX));
    setOpenConnectModal(true);
  };

  const isCardVariant = variant === "card";
  const shouldShowBackground = isCardVariant;
  const padding = variant === "plain" ? "none" : "sm";

  return (
    <InfoCard
      title={title}
      padding={padding}
      withBackground={shouldShowBackground}
      withBorder={shouldShowBackground}
      gap={title ? "md" : "sm"}
      className={cn("flex w-full flex-col overflow-hidden", isCardVariant ? "md:p-5" : "p-0 rounded-none", className)}
      titleClassName={titleClassName}
      contentClassName="flex min-h-0 flex-1 flex-col gap-0 overflow-hidden"
      style={style}
    >
      <LinkExchangesList
        className={cn("min-h-0 flex-1 overflow-y-auto", innerClassName)}
        exchangeIds={supportedCexs}
        handleSelectCex={handleSelectCex}
        showRemoveButton
        showCampaignCounts={showCampaignCounts}
        campaignCountsByExchange={campaignCountsByExchange}
        sortByCampaigns={sortByCampaigns}
        isStepActive={isStepActive}
      />
    </InfoCard>
  );
}
